// ProductDetail.js

import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { StoreContext } from '../contexts/StoreContext';
import { CartContext } from '../contexts/CartContext';
import { FavoritesContext } from '../contexts/FavoritesContext';
import { ProductsContext } from '../contexts/ProductsContext';
import Loading from './Loading';
import SuggestedProductContent from './SuggestedProductContent';
import usePageView from '../hooks/usePageView';
import { showPrices, showLocalPickupBanner } from '../constants/settings';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
  SPACING_PAGE,
} from '../constants/layout';
import { formatPrice, getFormattedProductPrice } from '../utils/priceFormatter';
import { StockStatus, isInStock } from '../constants/stockStatus';
import {
  FONT_LARGE_TITLE_MOBILE,
  FONT_TITLE3,
  FONT_HEADLINE,
  FONT_SUBHEADLINE,
  FONT_BODY,
  FONT_CALLOUT,
  FONT_FOOTNOTE,
  FONT_CAPTION2,
} from '../constants/typography';

const formatDate = timestamp => {
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

function FadeInImage({ src, alt, onClick, className = '' }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className={`image-container ${className}`}>
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
      />
    </div>
  );
}

function MediaDisplay({ item, alt, onClick, className = '' }) {
  const [loaded, setLoaded] = useState(false);
  const isVideo = item.mediaType === 'video';

  if (isVideo) {
    return (
      <div className={`image-container ${className}`} onClick={onClick}>
        <video
          src={item.imageURL}
          onLoadedData={() => setLoaded(true)}
          className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
          muted
          playsInline
        />
        <div className="video-indicator">
          <span className="material-icons play-icon">play_circle</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`image-container ${className}`}>
      <img
        src={item.imageURL}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
      />
    </div>
  );
}

// Reusable collapsible section component
function CollapsibleSection({ title, isExpanded, onToggle, children }) {
  return (
    <div className="description-section">
      <div className="description-header" onClick={onToggle}>
        <h3 className="small-section-title">{title}</h3>
        <span className="material-icons arrow-icon">
          {isExpanded ? 'expand_less' : 'expand_more'}
        </span>
      </div>
      <hr className="section-divider" />
      {isExpanded && children}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  usePageView('product', id);
  const { store, loading: storeLoading } = useContext(StoreContext);
  const { addToCart, items: cartItems } = useContext(CartContext);
  const { addFavorite, removeFavorite, isFavorite } = useContext(FavoritesContext);
  const { products: allProducts, imageItems: allImages, getCoverImage } = useContext(ProductsContext);
  const { showRelated } = useDisplayPreferences();
  const storePhone = store?.storeContact?.phoneNumber ?? '';
  const storeEmail = store?.storeContact?.email ?? '';

  const [product, setProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const [allCategories, setAllCategories] = useState([]);
  const [specDefinitions, setSpecDefinitions] = useState([]);
  const [specGroups, setSpecGroups] = useState([]);
  // const [loadedImages, setLoadedImages] = useState({});

  // Default to expanded
  const [showDescription, setShowDescription] = useState(true);
  // Default to expanded
  const [showIdentifiers, setShowIdentifiers] = useState(true);
  // Default to expanded for contact
  const [showContact, setShowContact] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSpecGroups, setExpandedSpecGroups] = useState({});

  // Cart-related state
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [addToCartStatus, setAddToCartStatus] = useState(null); // null | 'adding' | 'added'
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!product?.modifierGroups) return;
    setExpandedGroups(prev => {
      const updated = { ...prev };
      let changed = false;
      product.modifierGroups.forEach(group => {
        if (updated[group.id] === undefined) {
          updated[group.id] = true;
          changed = true;
        }
      });
      const validIds = new Set(product.modifierGroups.map(group => group.id));
      Object.keys(updated).forEach(id => {
        if (!validIds.has(id)) {
          delete updated[id];
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [product?.modifierGroups]);

  // Initialize spec groups as expanded when specs are loaded
  useEffect(() => {
    if (!product?.specs) return;
    setExpandedSpecGroups(prev => {
      const updated = { ...prev };
      let changed = false;

      // Get all unique group IDs from specs
      const groupIds = new Set();
      Object.keys(product.specs).forEach(specId => {
        const definition = specDefinitions.find(d => d.id === specId);
        if (definition) {
          const groupId = definition.groupId || 'ungrouped';
          groupIds.add(groupId);
          if (updated[groupId] === undefined) {
            updated[groupId] = true;
            changed = true;
          }
        }
      });

      // Remove groups that are no longer valid
      Object.keys(updated).forEach(id => {
        if (!groupIds.has(id)) {
          delete updated[id];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [product?.specs, specDefinitions]);

  // Fetch the main product
  useEffect(() => {
    async function loadProduct() {
      const ref = doc(db, 'products', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProduct({ id: snap.id, ...snap.data() });
      }
    }
    loadProduct();
  }, [id]);

  // Filter images for THIS product from ProductsContext
  useEffect(() => {
    if (!id || !allImages) return;

    const filtered = allImages.filter(img => img.productID === id);
    setProductImages(filtered);

    let cover = null;
    if (product?.productImage?.imageItemID) {
      cover = allImages.find(
        img => img.id === product.productImage.imageItemID
      );
    }
    if (!cover && filtered.length > 0) {
      cover = filtered[0];
    }
    setCoverImage(cover);
  }, [id, allImages, product?.productImage?.imageItemID]);

  // Update document title
  useEffect(() => {
    if (product) {
      document.title = product.productName;
    }
  }, [product]);

  // Fetch ALL categories (once)
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'categories'));
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllCategories(fetched);
    })();
  }, []);

  // Fetch spec definitions
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'spec_definitions'));
      const fetched = snap.docs.map(d => {
        const data = d.data();
        // Convert options from {id: value} to [{id, value}]
        let options = null;
        if (data.options) {
          options = Object.entries(data.options).map(([id, value]) => ({ id, value }));
        }
        return {
          id: d.id,
          label: data.label || '',
          dataType: data.data_type || 'string',
          groupId: data.group_id || null,
          options
        };
      });
      setSpecDefinitions(fetched);
    })();
  }, []);

  // Fetch spec groups
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'spec_groups'));
      const fetched = snap.docs.map(d => ({
        id: d.id,
        label: d.data().label || ''
      }));
      setSpecGroups(fetched);
    })();
  }, []);

  // Modal: ESC key listener & body overflow
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape' && previewImage) {
        closeModal();
      }
    };

    if (previewImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [previewImage]);

  const handleImageClick = image => {
    setPreviewImage(image);
    setImageLoading(true);
    setTimeout(() => {
      setIsModalVisible(true);
    }, 10);
  };

  const handleModalImageLoad = () => {
    setImageLoading(false);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setImageLoading(false);
    setTimeout(() => {
      setPreviewImage(null);
    }, 200);
  };

  const handleModalClick = e => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const toggleModifierGroup = groupId => {
    setExpandedGroups(prev => {
      const isExpanded = prev?.[groupId] ?? true;
      return { ...prev, [groupId]: !isExpanded };
    });
  };

  const toggleSpecGroup = groupId => {
    setExpandedSpecGroups(prev => {
      const isExpanded = prev?.[groupId] ?? true;
      return { ...prev, [groupId]: !isExpanded };
    });
  };

  // Calculate total quantity already in cart for this product
  const getQuantityInCart = () => {
    if (!product) return 0;
    return cartItems
      .filter(item => item.productID === product.id)
      .reduce((total, item) => total + item.quantity, 0);
  };

  const quantityInCart = getQuantityInCart();

  // Handle adding product to cart
  const handleAddToCart = () => {
    if (!product) return;

    setAddToCartStatus('adding');

    // Prepare product with image URL
    const productWithImage = {
      ...product,
      imageURL: coverImage?.imageURL || product.imageURL,
    };

    // Add to cart with current selections and quantity
    addToCart(productWithImage, selectedModifiers, quantity);

    // Show success state
    setTimeout(() => {
      setAddToCartStatus('added');
    }, 600);

    // Reset to normal state and reset quantity
    setTimeout(() => {
      setAddToCartStatus(null);
      setQuantity(1);
    }, 1500);
  };

  // Check if product is deactivated
  const isDeactivated = product?.deactivatedDate != null;

  // Check if product is in stock using enum
  const productIsInStock = isInStock(product?.stockStatus);

  // Check if all required modifiers are selected
  const areRequiredModifiersSelected = () => {
    if (!product?.modifierGroups || product.modifierGroups.length === 0) {
      return true;
    }

    return product.modifierGroups.every(group => {
      const picked = selectedModifiers.filter(m => m.groupID === group.id).length;
      const minRequired = group.quantityInfo?.minPermitted || 0;
      return picked >= minRequired;
    });
  };

  // Check if add to cart button should be disabled
  const isAddToCartDisabled =
    addToCartStatus === 'adding' ||
    isDeactivated ||
    !productIsInStock ||
    !areRequiredModifiersSelected();

  // Handle favorite toggle
  const handleFavoriteToggle = () => {
    if (!product) return;

    if (isFavorite(product.id)) {
      removeFavorite(product.id);
    } else {
      addFavorite(product.id);
    }
  };

  const isProductFavorited = product ? isFavorite(product.id) : false;

  // const handleImageLoad = productId => {
  //   setLoadedImages(prev => ({ ...prev, [productId]: true }));
  // };

  // Helper: format spec value based on data type
  const formatSpecValue = (value, definition) => {
    if (!definition) return String(value);

    switch (definition.dataType) {
      case 'boolean':
        return value ? 'Yes' : 'No';

      case 'number':
        return String(value);

      case 'string':
        return String(value);

      case 'single_select':
        if (definition.options) {
          const option = definition.options.find(opt => opt.id === value);
          return option ? option.value : value;
        }
        return value;

      case 'multi_select':
        if (Array.isArray(value) && definition.options) {
          return value
            .map(id => {
              const option = definition.options.find(opt => opt.id === id);
              return option ? option.value : id;
            })
            .join(', ');
        }
        return Array.isArray(value) ? value.join(', ') : value;

      default:
        return String(value);
    }
  };

  const isLoading = !product;

  const emailSubject = product
    ? encodeURIComponent(`Question about ${product.productName}`)
    : '';

  // Build a map: productID → [categoryID, …]
  const productToCategories = {};
  allCategories.forEach(cat => {
    (cat.itemIDs || []).forEach(pid => {
      if (!productToCategories[pid]) productToCategories[pid] = [];
      productToCategories[pid].push(cat.id);
    });
  });

  // Current product's categories (array of category IDs)
  const currCats = product ? productToCategories[product.id] || [] : [];

  // Current product's price
  const currPrice = product?.priceInfo?.price ?? 0;

  // Compute min/max price over all products
  const prices = allProducts.map(p => (typeof p.priceInfo?.price === 'number' ? p.priceInfo.price : 0));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  // Weights (sum should be 1)
  const weightPrice = 0.4;
  const weightCategory = 0.6;

  // Compute "suggestedProducts" by price / category similarity
  const suggestedProducts = product
    ? allProducts
        .filter(p => p.id !== product.id)
        .map(p => {
          const otherPrice = typeof p.priceInfo?.price === 'number' ? p.priceInfo.price : 0;
          const priceDiff = Math.abs(currPrice - otherPrice);
          const priceNormalized = (maxPrice - minPrice) > 0
            ? priceDiff / (maxPrice - minPrice)
            : 0;
          const priceScore = 1 - priceNormalized;

          const otherCats = productToCategories[p.id] || [];
          const sharedCount = currCats.filter(c => otherCats.includes(c)).length;
          const unionCount = new Set([...currCats, ...otherCats]).size;
          const catScore = unionCount > 0 ? sharedCount / unionCount : 0;

          const combinedScore = weightPrice * priceScore + weightCategory * catScore;
          return { ...p, combinedScore };
        })
        .sort((a, b) => b.combinedScore - a.combinedScore)
        .slice(0, 4)
    : [];

  return (
    <div className="product-detail-container">
      {isLoading ? (
        <Loading />
      ) : (
        <>
      {/* Main Product Section */}
      <div className="product-main-section">
        {/* Left Column: Images */}
        <div className="images-column">
          {/* Cover Image */}
          {coverImage?.imageURL ? (
            <div className="cover-image-container">
              <MediaDisplay
                item={coverImage}
                alt={product.productName}
                onClick={() => handleImageClick(coverImage)}
                className="cover-image"
              />
            </div>
          ) : (
            <div className="cover-image-container">
              <div className="cover-placeholder">
                <span className="material-icons placeholder-icon">photo</span>
              </div>
            </div>
          )}

          {/* More Photos Gallery */}
          {productImages.length > 1 && (
            <div className="gallery-section">
              <div className="product-image-grid">
                {productImages.map(img => (
                  <div key={img.id} className="product-image-item">
                    <MediaDisplay
                      item={img}
                      alt={img.caption}
                      onClick={() => handleImageClick(img)}
                      className="gallery-image thumbnail-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Product Info */}
        <div className="product-info">
          {/* Local Pickup Banner */}
          {showLocalPickupBanner && (
            <div className="pickup-banner">
              <span className="material-icons pickup-icon">store</span>
              <span className="pickup-text">Local pickup only • Shipping not available</span>
            </div>
          )}

          {/* ← Insert categories here: */}
          {currCats.length > 0 && (
            <div className="product-categories">
              {currCats.map(catId => {
                const catObj = allCategories.find(c => c.id === catId);
                return catObj ? (
                  <Link
                    key={catObj.id}
                    to={`/category/${catObj.id}`}
                    className="category-label"
                  >
                    {catObj.title}
                  </Link>
                ) : null;
              })}
            </div>
          )}

          {/* Product name and favorite button */}
          <div className="product-title-row">
            <h1 className="product-title">{product.productName}</h1>
            {/* Temporarily commented out favorite button
            <button
              onClick={handleFavoriteToggle}
              className="favorite-button"
              aria-label={isProductFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <span className="material-icons">
                {isProductFavorited ? 'favorite' : 'favorite_border'}
              </span>
            </button>
            */}
          </div>

          {showPrices && getFormattedProductPrice(product) ? (
            <p className="product-price">
              {getFormattedProductPrice(product)}
            </p>
          ) : null}

          {product.stockStatus && product.stockStatus !== StockStatus.IN_STOCK && (
            <p className="stock-status">{product.stockStatus}</p>
          )}

          {/* Temporarily commented out Add to Cart Section
          <div className="add-to-cart-section">
            {(isDeactivated || !productIsInStock) && (
              <p className="stock-message">
                {isDeactivated ? 'Unavailable' : (product.stockStatus || StockStatus.OUT_OF_STOCK)}
              </p>
            )}

            <div className="cart-controls-row">
              {productIsInStock && !isDeactivated && (
                <div className="quantity-controls">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="quantity-button"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="quantity-display">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="quantity-button"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              )}

              <button
                className="add-to-cart-button"
                onClick={handleAddToCart}
                disabled={isAddToCartDisabled}
              >
                {addToCartStatus === 'adding' ? (
                  <span className="button-content">
                    <span className="spinner"></span>
                    Adding...
                  </span>
                ) : addToCartStatus === 'added' ? (
                  <span className="button-content">
                    <span className="material-icons check-icon">check_circle</span>
                    Added!
                  </span>
                ) : (
                  <span className="button-content-column">
                    <span className="button-main-row">
                      {productIsInStock && !isDeactivated && (
                        <span className="material-icons plus-icon">add</span>
                      )}
                      {isDeactivated || !productIsInStock ? 'Sold Out' : 'Add to Cart'}
                    </span>
                    {quantityInCart > 0 && productIsInStock && !isDeactivated && (
                      <span className="cart-quantity-badge">
                        {quantityInCart} in cart
                      </span>
                    )}
                  </span>
                )}
              </button>
            </div>
          </div>
          */}

          {/* Collapsible Description */}
          {product.description && product.description.trim() !== '' && (
            <CollapsibleSection
              title="Description"
              isExpanded={showDescription}
              onToggle={() => setShowDescription(prev => !prev)}
            >
              <p className="product-description">{product.description}</p>
            </CollapsibleSection>
          )}

          {Array.isArray(product.modifierGroups) &&
            product.modifierGroups.length > 0 && (
              <div className="modifier-groups-section">
                {product.modifierGroups.map(group => {
                  const isExpanded = expandedGroups[group.id] ?? true;
                  return (
                    <CollapsibleSection
                      key={group.id}
                      title={group.title}
                      isExpanded={isExpanded}
                      onToggle={() => toggleModifierGroup(group.id)}
                    >
                      {Array.isArray(group.modifierOptions) && (
                        <ul className="modifier-options-list">
                          {group.modifierOptions.map(option => {
                            const priceInfo = option.priceInfo || {};
                            const showOptionPrice =
                              showPrices &&
                              typeof priceInfo.price === 'number' &&
                              priceInfo.price > 0 &&
                              priceInfo.currency;
                            const stockStatus =
                              typeof option.stockStatus === 'string'
                                ? option.stockStatus.trim()
                                : '';
                            const isOptionOutOfStock = stockStatus === StockStatus.OUT_OF_STOCK;
                            return (
                              <li key={option.id} className="modifier-option-item">
                                <div className="modifier-option-header">
                                  <span className="modifier-option-title">
                                    {option.title}
                                  </span>
                                  {showOptionPrice && (
                                    <span className="modifier-option-price">
                                      {formatPrice(priceInfo.price, priceInfo.currency)}
                                    </span>
                                  )}
                                </div>
                                {isOptionOutOfStock && (
                                  <p className="modifier-option-stock modifier-option-stock--out">
                                    {stockStatus || option.stockStatus}
                                  </p>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </CollapsibleSection>
                  );
                })}
              </div>
            )}

          {/* ----------------- "Product Specs" SECTIONS (each group is its own section) ----------------- */}
          {product.specs && Object.keys(product.specs).length > 0 && (() => {
            // Build groups: { groupId: [...specs], null: [...ungrouped specs] }
            const grouped = {};
            Object.entries(product.specs).forEach(([specId, value]) => {
              const definition = specDefinitions.find(d => d.id === specId);
              if (definition) {
                const groupId = definition.groupId || 'ungrouped';
                if (!grouped[groupId]) grouped[groupId] = [];
                grouped[groupId].push({ specId, value, definition });
              }
            });

            return Object.entries(grouped).map(([groupId, specs]) => {
              const group = groupId !== 'ungrouped' ? specGroups.find(g => g.id === groupId) : null;
              const title = group ? group.label : 'Specifications';
              const isExpanded = expandedSpecGroups[groupId] ?? true;

              return (
                <CollapsibleSection
                  key={groupId}
                  title={title}
                  isExpanded={isExpanded}
                  onToggle={() => toggleSpecGroup(groupId)}
                >
                  <ul className="product-specs-list">
                    {specs.map(({ specId, value, definition }) => (
                      <li key={specId}>
                        <strong>{definition.label}:</strong>{' '}
                        {formatSpecValue(value, definition)}
                      </li>
                    ))}
                  </ul>
                </CollapsibleSection>
              );
            });
          })()}

          {/* ----------------- "Identifiers" SECTION (only show non-empty rows) ----------------- */}
          {product.identifiers && (
            <CollapsibleSection
              title="Identifiers"
              isExpanded={showIdentifiers}
              onToggle={() => setShowIdentifiers(prev => !prev)}
            >
              <ul className="product-specs-list">
                {product.identifiers.sku && product.identifiers.sku.trim() !== '' && (
                  <li>
                    <strong>SKU:</strong> {product.identifiers.sku}
                  </li>
                )}
                {product.identifiers.upc && product.identifiers.upc.trim() !== '' && (
                  <li>
                    <strong>UPC:</strong> {product.identifiers.upc}
                  </li>
                )}
                {product.identifiers.ean && product.identifiers.ean.trim() !== '' && (
                  <li>
                    <strong>EAN:</strong> {product.identifiers.ean}
                  </li>
                )}
                {product.identifiers.gtin && product.identifiers.gtin.trim() !== '' && (
                  <li>
                    <strong>GTIN:</strong> {product.identifiers.gtin}
                  </li>
                )}
              </ul>
            </CollapsibleSection>
          )}

          {product.deactivatedDate && (
            <p className="deactivated-date">
              No longer available as of {formatDate(product.deactivatedDate)}
            </p>
          )}

          {/* Contact Section */}
          {(storePhone || storeEmail) && (
            <CollapsibleSection
              title="Have more questions?"
              isExpanded={showContact}
              onToggle={() => setShowContact(!showContact)}
            >
              <div className="contact-button-row">
                {storePhone && (
                  <button
                    type="button"
                    className="call-store-button"
                    onClick={() => {
                      if (typeof window.fbq === 'function') window.fbq('track', 'Contact');
                      const digitsOnly = storePhone.replace(/\D+/g, '');
                      window.location.href = `tel:${digitsOnly}`;
                    }}
                    disabled={storeLoading}
                  >
                    {storeLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <span className="material-icons phone-icon">
                          phone
                        </span>
                        Call
                      </>
                    )}
                  </button>
                )}
                {storePhone && (
                  <button
                    type="button"
                    className="text-store-button"
                    onClick={() => {
                      if (typeof window.fbq === 'function') window.fbq('track', 'Contact');
                      const digitsOnly = storePhone.replace(/\D+/g, '');
                      window.location.href = `sms:${digitsOnly}`;
                    }}
                    disabled={storeLoading}
                  >
                    {storeLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <span className="material-icons text-icon">
                          textsms
                        </span>
                        Text
                      </>
                    )}
                  </button>
                )}
                {storeEmail && (
                  <button
                    type="button"
                    className="email-store-button"
                    onClick={() => {
                      if (typeof window.fbq === 'function') window.fbq('track', 'Contact');
                      window.location.href = `mailto:${storeEmail}?subject=${emailSubject}`;
                    }}
                    disabled={storeLoading}
                  >
                    {storeLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <span className="material-icons email-icon">mail</span>
                        Email
                      </>
                    )}
                  </button>
                )}
              </div>
            </CollapsibleSection>
          )}
        </div>
      </div>

      {/* Suggested Products Section */}
      {showRelated && suggestedProducts.length > 0 && (
        <div className="suggested-section">
          <div className="section-header">
            <h2 className="section-title">You May Also Like</h2>
            <hr className="section-divider" />
          </div>

          <div className="suggested-grid">
            {suggestedProducts.map(sugg => (
              <div key={sugg.id} className="suggested-item">
                <Link to={`/product/${sugg.id}`} className="suggested-link">
                  <SuggestedProductContent
                    product={sugg}
                    getCoverImageForProduct={getCoverImage}
                  />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image/Video Preview Modal */}
      {previewImage && (
        <div
          className={`modal-overlay ${
            isModalVisible ? 'modal-visible' : ''
          }`}
          onClick={handleModalClick}
        >
          <button
            className="modal-close-button"
            onClick={closeModal}
            aria-label="Close preview"
          >
            ×
          </button>

          <div
            className={`modal-content ${
              isModalVisible ? 'modal-content-visible' : ''
            }`}
          >
            {imageLoading && (
              <div className="modal-placeholder">
                <div className="placeholder-spinner"></div>
                <p className="placeholder-text">Loading {previewImage.mediaType === 'video' ? 'video' : 'image'}...</p>
              </div>
            )}

            {previewImage.mediaType === 'video' ? (
              <video
                src={previewImage.imageURL}
                controls
                autoPlay
                className={`modal-image ${
                  imageLoading ? 'modal-image-loading' : ''
                }`}
                onLoadedData={handleModalImageLoad}
              />
            ) : (
              <img
                src={previewImage.imageURL}
                alt={previewImage.caption || product.productName}
                className={`modal-image ${
                  imageLoading ? 'modal-image-loading' : ''
                }`}
                onLoad={handleModalImageLoad}
              />
            )}
            {previewImage.caption && !imageLoading && (
              <p className="modal-caption">{previewImage.caption}</p>
            )}
          </div>
        </div>
      )}

        </>
      )}
      <style jsx>{`
        /* Container */
        .product-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 767px) {
          .product-detail-container {
            padding: ${SPACING_SECTION} ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }
        }

        /* Main Section: flex-column on mobile, row on desktop */
        .product-main-section {
          display: flex;
          gap: ${SPACING_CARD};
          margin-bottom: ${SPACING_HERO};
          flex-direction: column;
        }
        @media (min-width: 768px) {
          .product-main-section {
            flex-direction: row;
            align-items: flex-start;
          }
        }

        /* Images Column - Contains both cover image and gallery */
        .images-column {
          flex: 1;
          max-width: 500px;
          display: flex;
          flex-direction: column;
          gap: ${SPACING_SECTION};
        }
        @media (max-width: 767px) {
          .images-column {
            max-width: none;
            width: 100%;
          }
        }

        /* Cover Image Container */
        .cover-image-container {
          width: 100%;
        }

        /* Placeholder when no cover image */
        .cover-placeholder {
          position: relative;
          width: 100%;
          background-color: var(--color-border);
          border: 1px solid var(--color-border-strong);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        .cover-placeholder::before {
          content: '';
          display: block;
          padding-top: 133.33%;
        }
        .cover-placeholder .placeholder-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 64px;
          opacity: 0.5;
          line-height: 1;
        }

        /* Image-Container & Fade-In */
        .image-container {
          position: relative;
          width: 100%;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-radius: 0;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--color-border-strong);
        }
        .image-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }
        .image-container::before {
          content: '';
          display: block;
          padding-top: 133.33%;
        }
        /* Thumbnails */
        .thumbnail-image::before {
          padding-top: 100% !important;
        }
        .fade-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        .fade-img.loaded {
          opacity: 1;
        }
        /* Video elements */
        video.fade-img {
          background: #000;
        }
        .clickable-image {
          cursor: pointer;
        }

        /* Video Indicator Overlay */
        .video-indicator {
          position: absolute;
          bottom: 8px;
          left: 8px;
          background: rgba(0, 0, 0, 0.6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }
        .play-icon {
          font-size: 32px;
          color: white;
          line-height: 1;
        }

        /* Product Info (Title, Price) */
        .product-info {
          flex: 1;
          min-width: 0;
        }
        @media (min-width: 768px) {
          .product-info {
            /* no extra padding */
          }
        }

        /* Local Pickup Banner */
        .pickup-banner {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPACT};
          background-color: #f0f4f8;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          padding: ${SPACING_COMPONENT} ${SPACING_BUTTON};
          margin-bottom: ${SPACING_CARD};
          font-size: ${FONT_CALLOUT};
          color: #2d3748;
          width: 100%;
          box-sizing: border-box;
        }
        .pickup-icon {
          font-size: 20px;
          line-height: 1;
          color: #4a5568;
        }
        .pickup-text {
          line-height: 1.4;
          font-weight: 500;
        }

        /* New: Category labels just above title */
        .product-categories {
          margin-bottom: ${SPACING_COMPACT};
        }
        .category-label {
          display: inline-block;
          font-size: ${FONT_FOOTNOTE};
          font-weight: 500;
          color: var(--color-text-muted);
          margin-right: ${SPACING_COMPONENT};
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .category-label:hover,
        .category-label:focus {
          color: var(--color-text-secondary);
          text-decoration: underline;
        }

        .product-title-row {
          display: flex;
          align-items: flex-start;
          gap: ${SPACING_BUTTON};
          margin-bottom: ${SPACING_BUTTON};
        }

        .product-title {
          margin: 0;
          font-size: ${FONT_LARGE_TITLE_MOBILE};
          font-weight: 700;
          line-height: 1.2;
          color: var(--color-text-primary);
          flex: 1;
        }

        .favorite-button {
          flex-shrink: 0;
          width: 44px;
          height: 44px;
          border: 1px solid var(--color-border);
          background-color: var(--color-surface);
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          padding: 0;
        }

        .favorite-button:hover:not(:disabled) {
          background-color: var(--color-surface-muted);
          border-color: var(--color-accent);
          transform: scale(1.05);
        }

        .favorite-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .favorite-button .material-icons {
          font-size: 24px;
          color: var(--color-accent);
          transition: color 0.2s ease;
        }

        .favorite-button:hover:not(:disabled) .material-icons {
          color: var(--color-accent-hover);
        }

        .product-price {
          margin: 0 0 ${SPACING_CARD} 0;
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          color: var(--color-accent);
        }

        /* Stock Status & Contact Buttons */
        .stock-status {
          margin: ${SPACING_COMPACT} 0;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-muted);
        }
        .contact-button-row {
          display: flex;
          flex-wrap: wrap;
          gap: ${SPACING_BUTTON};
          margin-top: ${SPACING_BUTTON};
        }
        .call-store-button,
        .text-store-button,
        .email-store-button {
          background-color: var(--color-accent);
          border: none;
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          font-size: ${FONT_HEADLINE};
          color: var(--color-accent-text);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_COMPACT};
        }
        .call-store-button:hover:not(:disabled),
        .text-store-button:hover:not(:disabled),
        .email-store-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
        }
        .call-store-button:disabled,
        .text-store-button:disabled,
        .email-store-button:disabled {
          opacity: 0.6;
          cursor: default;
          background-color: var(--color-badge-muted);
        }
        .phone-icon,
        .text-icon,
        .email-icon {
          font-size: 20px;
          line-height: 1;
        }
        /* Collapsible Description */
        .description-section {
          margin-top: ${SPACING_CARD};
          margin-bottom: ${SPACING_CARD};
        }
        .description-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          margin-bottom: ${SPACING_COMPACT}; /* extra space between header text and divider */
        }
        .small-section-title {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .arrow-icon {
          font-size: 24px;
          line-height: 1;
        }
        /* Divider now solid */
        .section-divider {
          margin: 0 0 ${SPACING_BUTTON} 0;
          height: 2px;
          border: 0;
          background-color: var(--color-border);
        }
        .product-description {
          margin: ${SPACING_BUTTON} 0 ${SPACING_CARD} 0;
          font-size: ${FONT_BODY};
          line-height: 1.6;
          color: var(--color-text-secondary);
        }

        /* New: Product Specifications List */
        .product-specs-list {
          margin: ${SPACING_BUTTON} 0 ${SPACING_CARD} 0;
          padding-left: ${SPACING_CARD}; /* indent list items */
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          line-height: 1.6;
        }
        .product-specs-list li {
          margin-bottom: ${SPACING_COMPACT};
        }
        .product-specs-list li strong {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .modifier-options-list {
          margin: ${SPACING_BUTTON} 0 ${SPACING_CARD} 0;
          padding: 0;
          list-style: none;
        }
        .modifier-option-item {
          padding: ${SPACING_BUTTON} 0;
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .modifier-option-item:last-child {
          border-bottom: none;
        }
        .modifier-option-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: ${SPACING_BUTTON};
        }
        .modifier-option-title {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .modifier-option-price {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          white-space: nowrap;
        }
        .modifier-option-stock {
          margin: ${SPACING_ELEMENT} 0 0 0;
          font-size: ${FONT_CALLOUT};
          color: var(--color-text-secondary);
        }
        .modifier-option-stock--out {
          color: var(--color-text-muted);
        }

        .deactivated-date {
          margin: ${SPACING_COMPACT} 0;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-muted);
        }

        /* Add to Cart Section */
        .add-to-cart-section {
          margin-top: ${SPACING_SECTION};
        }

        .signed-out-notice {
          background-color: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_CARD};
          margin-bottom: ${SPACING_CARD};
        }

        .notice-content {
          display: flex;
          align-items: center;
          gap: ${SPACING_BUTTON};
        }

        .notice-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .notice-text {
          flex: 1;
        }

        .notice-title {
          font-size: ${FONT_BODY};
          font-weight: 600;
          margin: 0 0 ${SPACING_TIGHT} 0;
          color: #92400e;
        }

        .notice-description {
          font-size: ${FONT_CALLOUT};
          margin: 0;
          color: #92400e;
        }

        .stock-message {
          text-align: center;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          margin-bottom: ${SPACING_BUTTON};
        }

        .cart-controls-row {
          display: flex;
          align-items: stretch;
          gap: ${SPACING_BUTTON};
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPACT};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_TIGHT};
          background-color: var(--color-surface-muted);
          height: 54px;
        }

        .quantity-button {
          width: 36px;
          height: 36px;
          border: none;
          background-color: transparent;
          color: var(--color-text-primary);
          font-size: 18px;
          cursor: pointer;
          border-radius: 4px;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .quantity-button:hover {
          background-color: var(--color-border);
        }

        .quantity-display {
          min-width: 40px;
          text-align: center;
          font-weight: 600;
          color: var(--color-text-primary);
          font-size: ${FONT_BODY};
        }

        .add-to-cart-button {
          flex: 1;
          padding: ${SPACING_BUTTON} ${SPACING_GROUP};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY};
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 54px;
        }

        .add-to-cart-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
          transform: translateY(-1px);
        }

        .add-to-cart-button:disabled {
          background-color: var(--color-surface-muted);
          color: var(--color-text-secondary);
          cursor: not-allowed;
          opacity: 0.6;
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_COMPACT};
        }

        .button-content-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_TIGHT};
        }

        .button-main-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_COMPACT};
        }

        .cart-quantity-badge {
          font-size: ${FONT_CAPTION2};
          font-weight: 500;
          color: var(--color-accent-text);
          opacity: 0.9;
        }

        .plus-icon {
          font-size: 20px;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid var(--color-accent-text);
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        .check-icon {
          font-size: 20px;
          color: var(--color-accent-text);
          line-height: 1;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Gallery Section */
        .gallery-section {
          margin-top: 0;
        }
        .product-image-grid {
          display: grid;
          gap: ${SPACING_BUTTON};
          grid-template-columns: repeat(4, 1fr);
        }
        @media (min-width: 600px) {
          .product-image-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: ${SPACING_BUTTON};
          }
        }
        @media (max-width: 480px) {
          .product-image-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: ${SPACING_COMPONENT};
          }
        }
        .product-image-item {
          display: flex;
          flex-direction: column;
        }

        /* Suggested Products Section */
        .suggested-section {
          margin-top: ${SPACING_HERO};
        }
        .suggested-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: ${SPACING_BUTTON};
          width: 100%;
        }
        .suggested-item {
          display: flex;
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          transition: background-color 0.2s ease;
          overflow: hidden;
        }
        .suggested-item:hover {
          background-color: var(--color-card-background-hover);
        }
        .suggested-link {
          flex: 1;
          display: flex;
          align-items: center;
          padding: 0;
          text-decoration: none;
          color: inherit;
          width: 100%;
        }
        .suggested-product-content {
          display: flex;
          align-items: center;
          gap: 0;
          width: 100%;
        }
        .suggested-product-image {
          width: 75px;
          height: 75px;
          object-fit: cover;
          border-radius: 0;
          flex-shrink: 0;
          display: block;
        }
        .suggested-product-info {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
          padding: ${SPACING_BUTTON};
        }
        .suggested-product-name {
          flex: 1;
          min-width: 0;
          overflow-wrap: break-word;
          white-space: normal;
          margin-right: ${SPACING_BUTTON};
          font-size: ${FONT_BODY};
          font-weight: bold;
        }
        .suggested-product-price {
          flex-shrink: 0;
          font-weight: bold;
          color: var(--color-accent);
          font-size: ${FONT_CALLOUT};
        }

        /* Modal Overlay & Content */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: ${SPACING_CARD_BODY};
          opacity: 0;
          transition: opacity 0.2s ease, background-color 0.2s ease;
          pointer-events: none;
        }
        .modal-overlay.modal-visible {
          opacity: 1;
          background-color: rgba(0, 0, 0, 0.9);
          pointer-events: all;
        }

        .modal-close-button {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(0, 0, 0, 0.7);
          border: none;
          color: white;
          font-size: 32px;
          cursor: pointer;
          padding: ${SPACING_COMPACT} ${SPACING_BUTTON};
          z-index: 1001;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s ease, transform 0.2s.ease;
          line-height: 1;
        }
        .modal-close-button:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }

        .modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          transform: scale(0.8);
          opacity: 0;
          transition: transform 0.2s.ease, opacity 0.2s.ease;
        }
        .modal-content.modal-content-visible {
          transform: scale(1);
          opacity: 1;
        }

        .modal-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 300px;
          min-height: 200px;
          color: white;
        }
        .placeholder-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: ${SPACING_CARD};
        }
        .placeholder-text {
          font-size: ${FONT_BODY};
          opacity: 0.8;
          margin: 0;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .modal-image {
          max-width: 100%;
          max-height: 80vh;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: 0;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          border: 1px solid var(--color-border-strong);
          transition: opacity 0.2s ease;
        }
        .modal-image-loading {
          opacity: 0;
          position: absolute;
          pointer-events: none;
        }
        .modal-caption {
          margin-top: ${SPACING_CARD};
          color: white;
          font-size: ${FONT_BODY};
          text-align: center;
          max-width: 600px;
          line-height: 1.4;
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        .suggested-product-content,
        .suggested-product-info {
          min-width: 0;
        }

        .suggested-product-price {
          margin-left: auto;
        }
        @media (max-width: 600px) {
          .modal-close-button {
            top: 15px;
            right: 15px;
            font-size: 24px;
            width: 40px;
            height: 40px;
            padding: ${SPACING_ELEMENT} ${SPACING_COMPACT};
          }
          .modal-image {
            max-height: 75vh;
          }
          .modal-caption {
            font-size: ${FONT_CALLOUT};
            margin-top: ${SPACING_BUTTON};
          }
          .modal-placeholder {
            min-width: 250px;
            min-height: 150px;
          }
          .placeholder-spinner {
            width: 32px;
            height: 32px;
            margin-bottom: ${SPACING_BUTTON};
          }
          .placeholder-text {
            font-size: ${FONT_CALLOUT};
          }
        }
      `}</style>
    </div>
  );
}
